import React, { useState } from "react";
import { Box, Button, Menu, MenuItem, Stack, Typography } from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import dayjs, { Dayjs } from "dayjs";

interface TimeRange {
  label: string;
  days: number;
}

const ranges: TimeRange[] = [
  { label: "1W", days: 7 },
  { label: "1M", days: 30 },
  { label: "3M", days: 90 },
  { label: "6M", days: 180 },
  { label: "1Y", days: 365 },
  { label: "5Y", days: 1826 },
];

export default function QuickDateRangePicker({
  onChange,
}: {
  onChange?: (start: Dayjs | undefined, end: Dayjs | undefined ) => void;
}) {
  // 根据屏幕宽度动态决定 visibleRanges
  const isSmallScreen = typeof window !== "undefined" && window.matchMedia("(max-width: 640px)").matches;
  const [visibleRanges, setVisibleRanges] = useState(() =>
    isSmallScreen ? ranges.slice(0, 1) : ranges.slice(0, 5)
  );
  const [activeRange, setActiveRange] = useState<any>();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleSelect = (range: TimeRange) => {
    const end = dayjs(); // 当前时间
    const start = end.subtract(range.days, "day"); // 减去指定天数

    setActiveRange(range);
    onChange && onChange(start, end);

    // 如果选择的是下拉菜单中的选项，替换显示列表的最后一项
    if (!visibleRanges.some((r) => r.days === range.days)) {
      setVisibleRanges([...visibleRanges.slice(0, 4), range]);
    }
    setAnchorEl(null);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  // 响应式：屏幕变化时动态调整 visibleRanges
  React.useEffect(() => {
    function handleResize() {
      const small = window.matchMedia("(max-width: 640px)").matches;
      setVisibleRanges(small ? ranges.slice(0, 1) : ranges.slice(0, 5));
    }
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        backgroundColor: "DateRangeQuickPicker.bg",
        p: 0.5,
        borderRadius: "0.25rem",
        height: "2rem",
      }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {visibleRanges.map((range) => (
          <Button
            key={range.days}
            variant={activeRange?.days === range.days ? "contained" : "text"}
            color={activeRange?.days === range.days ? "secondary" : "inherit"}
            onClick={() => handleSelect(range)}
            sx={{
              minWidth: 0,
              height: "1.5rem",
              padding: "0.125rem 0.5rem",
              borderRadius: "0.25rem",
              fontSize: "0.75rem",
              backgroundColor: activeRange?.days === range.days ? "DateRangeQuickPicker.button.bg" : "transparent",
              fontWeight: activeRange?.days === range.days ? "600" : "400",
              "&:hover": {
                backgroundColor: activeRange?.days === range.days 
                  ? "DateRangeQuickPicker.button.bg" 
                  : "action.hover"
              }
            }}>
            {range.label}
          </Button>
        ))}
      </Box>

      {ranges.length > 5 && (
        <>
          <Button
            variant="text"
            color="secondary"
            onClick={handleMenuOpen}
            sx={{ minWidth: 0, width: "0.75rem", height: "1.5rem", px: 1, borderRadius: "0.25rem" }}>
            <ArrowDropDownIcon sx={{ color: "text.tertiary" }} />
          </Button>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            sx={{
              "& .MuiPaper-root": {
                marginTop: "0.5rem",
                marginLeft: "0.25rem",
              },
            }}
            slotProps={{
              paper: {
                sx: {
                  backgroundColor: "background.paperExtreme",
                  backgroundImage: "none",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: "0.25rem",
                },
              },
            }}>
            {ranges
              .filter((range) => !visibleRanges.some((vr) => vr.days === range.days))
              .map((range) => (
                <MenuItem
                  key={range.days}
                  onClick={() => handleSelect(range)}
                  selected={activeRange?.days === range.days}
                  sx={{
                    minWidth: 0,
                    height: "2rem",
                    padding: "0.25rem 0.75rem",
                    borderRadius: "0.25rem",
                    fontSize: "0.875rem",
                    width: "3.5rem",
                    mx: "0.25rem",
                  }}>
                  {range.label}
                </MenuItem>
              ))}
          </Menu>
        </>
      )}
    </Box>
  );
}
